import { createContext, useContext, useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { scenes } from "../data/scenes";

// GA4 — gtag loaded via <Head> script tag; noop if unavailable
function gaEvent() {}

function getAuthToken() {
  return localStorage.getItem("fm_token");
}

// ——— Context ———
const PlayerContext = createContext(null);

/**
 * Single source of truth for all playback state + the one true <audio> element.
 * Three player UIs (PlayerBar, FullscreenPlayer, ImmersivePlayer) read from this context.
 * playerMode ∈ { 'bar' | 'fullscreen' | 'immersive' }
 */
export function PlayerProvider({
  children,
  isLoggedIn,
  likedTracks,
  favoritedTracks,
  showToast,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  // ── Audio ref ──
  // Keep a plain ref; the <audio> element is declared in JSX below.
  const audioRef = useRef(null);

  // ── Cross-tab sync via BroadcastChannel ──
  const channelRef = useRef(null);

  // ── Core playback state ──
  const TEMP_PLAYLIST_KEY = "fm_temp_playlist";
  const [currentTrack, setCurrentTrack] = useState(null);
  const [currentScene, setCurrentScene] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const stateRestoredRef = useRef(false); // set when fallbackRestore recovers saved state

  // ── Playlist ──
  const [playlist, setPlaylist] = useState([]);
  const [tempPlaylist, setTempPlaylist] = useState(() => {
    try {
      const saved = localStorage.getItem(TEMP_PLAYLIST_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [playlistName, setPlaylistName] = useState("");
  const [playlistSource, setPlaylistSource] = useState("playlist");
  const [loopMode, setLoopMode] = useState("repeat-all");
  const [exploreOffset, setExploreOffset] = useState(0);
  const [exploreTracks, setExploreTracks] = useState([]);
  const exploreIndexRef = useRef(0);
  const exploreSessionId = useRef(
    // eslint-disable-next-line react-hooks/purity
    "sess_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8)
  );
  const sharedTrackLoaded = useRef(false);

  // ── Persist tempPlaylist to localStorage ──
  useEffect(() => {
    try {
      localStorage.setItem(TEMP_PLAYLIST_KEY, JSON.stringify(tempPlaylist));
    } catch {
      // ignore localStorage errors
    }
  }, [tempPlaylist, TEMP_PLAYLIST_KEY]);

  // ── Three-player mode ──
  const [playerMode, setPlayerMode] = useState("bar");

  // ===== Helper =====
  function getSourceTracks() {
    if (playlistSource === "liked") {
      return Array.from(likedTracks.values())
        .filter((item) => item.track && item.track.id)
        .map((item) => ({ ...item.track, _scene: item.scene }));
    }
    if (playlistSource === "favorites") {
      return Array.from(favoritedTracks.values())
        .filter((item) => item.track && item.track.id)
        .map((item) => ({ ...item.track, _scene: item.scene }));
    }
    if (playlistSource === "temp") return tempPlaylist;
    if (playlistSource === "explore") return exploreTracks;
    return playlist;
  }

  function findSceneForTrack(track) {
    if (track._scene) return track._scene;
    return (
      scenes.find(
        (s) => s.id === track.scene_id || s.atmo === track.atmosphere
      ) || currentScene
    );
  }

  // ===== Play =====
  const playTrack = useCallback((track, scene) => {
    const audio = audioRef.current;

    // If track has no url, fetch it fresh from API
    if (!track.url) {
      fetch(`/api/tracks/${track.id}`)
        .then((r) => r.json())
        .then((fresh) => {
          setCurrentTrack(fresh);
          setCurrentScene(scene);
          if (audio) {
            console.log('[playTrack/fetched]', fresh.id, fresh.name || fresh.name_zh || '?', '→ URL:', fresh.url);
            audio.src = fresh.url;
            audio.play().then(() => {
              setIsPlaying(true);
              // 记录播放行为到云端（最近7天排除用）
              if (isLoggedIn) {
                const atmosphere = scene?.id || scene?.atmo || 'explore';
                fetch('/api/actions/', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${getAuthToken()}`,
                  },
                  body: JSON.stringify({ track_id: fresh.id, action: 'play', atmosphere }),
                }).catch(() => {});
              }
            }).catch(() => {});
          }
        })
        .catch(() => {});
      return;
    }

    setCurrentTrack(track);
    setCurrentScene(scene);

    if (!audio) {
      // Audio not mounted yet — defer until it is
      const tryPlay = () => {
        const a = audioRef.current;
        if (!a) return;
        const audioUrl = track.url;
        console.log('[playTrack/defer]', track.id, track.name || track.name_zh || '?', '→ URL:', audioUrl);
        a.src = audioUrl;
        a.play().then(() => {
          setIsPlaying(true);
          if (isLoggedIn) {
            const atmosphere = scene?.id || scene?.atmo || 'explore';
            fetch('/api/actions/', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getAuthToken()}`,
              },
              body: JSON.stringify({ track_id: track.id, action: 'play', atmosphere }),
            }).catch(() => {});
          }
        }).catch(() => {});
        gaEvent("play", track.name || track.id);
      };
      // Retry after a short delay (gives React time to mount <audio>)
      setTimeout(tryPlay, 100);
      return;
    }

    if (currentTrack && track.id !== currentTrack.id) {
      audio.pause();
    }

    const audioUrl = track.url;
    console.log('[playTrack/direct]', track.id, track.name || track.name_zh || '?', '→ URL:', audioUrl);
    audio.src = audioUrl;
    audio.play().then(() => {
      setIsPlaying(true);
      if (isLoggedIn) {
        const atmosphere = scene?.id || scene?.atmo || 'explore';
        fetch('/api/actions/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
          body: JSON.stringify({ track_id: track.id, action: 'play', atmosphere }),
        }).catch(() => {});
      }
    }).catch(() => {});
    gaEvent("play", track.name || track.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }

  // ===== Explore mode =====
  const fetchExploreNextRef = useRef(null);

  const fetchExploreNext = useCallback(async (forceReset) => {
    try {
      const offset = forceReset ? 0 : exploreOffset;
      const params = new URLSearchParams({
        offset: String(offset),
        limit: "20",
        session_id: exploreSessionId.current,
      });
      const headers = {};
      if (isLoggedIn) headers.Authorization = `Bearer ${getAuthToken()}`;
      const res = await fetch(`/api/recommendations/feed?${params}`, {
        headers,
      });
      if (!res.ok) {
        setIsPlaying(false);
        return;
      }
      const data = await res.json();
      if (data.tracks && data.tracks.length > 0) {
        setExploreTracks(data.tracks);
        setPlaylist(data.tracks);
        setExploreOffset((prev) => prev + data.tracks.length);
        exploreIndexRef.current = 0;
        playTrack(data.tracks[0], null);
      } else if (!forceReset) {
        // exhausted — reset and retry once
        setExploreOffset(0);
        exploreSessionId.current = "sess_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
        fetchExploreNextRef.current?.(true);
      } else {
        // Even after reset — no tracks at all
        setIsPlaying(false);
      }
    } catch {
      setIsPlaying(false);
    }
  }, [exploreOffset, isLoggedIn, playTrack]);

  // Keep ref in sync so recursive calls always use the latest closure
   
  useEffect(() => {
    fetchExploreNextRef.current = fetchExploreNext;
  });

  // ===== Next / Prev =====
  function nextTrack() {
    if (playlistSource === "explore") {
      const nextIdx = exploreIndexRef.current + 1;
      if (nextIdx < exploreTracks.length) {
        exploreIndexRef.current = nextIdx;
        playTrack(exploreTracks[nextIdx], null);
      } else {
        fetchExploreNext();
      }
      return;
    }
    const source = getSourceTracks();
    if (source.length === 0) return;
    const idx = source.findIndex((t) => t.id === currentTrack?.id);

    let nextIdx;
    if (loopMode === "shuffle") {
      if (source.length === 1) {
        nextIdx = 0;
      } else {
        do {
          nextIdx = Math.floor(Math.random() * source.length);
        } while (nextIdx === idx);
      }
    } else if (loopMode === "repeat-one") {
      nextIdx = idx >= 0 ? idx : 0;
    } else if (loopMode === "none") {
      if (idx < 0 || idx >= source.length - 1) return;
      nextIdx = idx + 1;
    } else {
      nextIdx = (idx + 1) % source.length;
    }

    if (nextIdx < 0 || nextIdx >= source.length) return;
    const next = source[nextIdx];
    const sc = findSceneForTrack(next);
    playTrack(next, sc);
  }

  function prevTrack() {
    if (playlistSource === "explore") {
      const prevIdx = exploreIndexRef.current - 1;
      if (prevIdx >= 0) {
        exploreIndexRef.current = prevIdx;
        playTrack(exploreTracks[prevIdx], null);
      }
      return;
    }

    const source = getSourceTracks();
    if (source.length === 0) return;
    const idx = source.findIndex((t) => t.id === currentTrack?.id);

    let prevIdx;
    if (loopMode === "shuffle") {
      if (source.length === 1) {
        prevIdx = 0;
      } else {
        do {
          prevIdx = Math.floor(Math.random() * source.length);
        } while (prevIdx === idx);
      }
    } else if (loopMode === "repeat-one") {
      prevIdx = idx >= 0 ? idx : 0;
    } else {
      prevIdx = (idx - 1 + source.length) % source.length;
    }

    if (prevIdx < 0 || prevIdx >= source.length) return;
    const prev = source[prevIdx];
    const sc = findSceneForTrack(prev);
    playTrack(prev, sc);
  }

  function handleEnded() {
    nextTrack();
  }

  // ===== Dislike =====
  function handleDislike() {
    const track = currentTrack;
    if (!track) return;
    if (isLoggedIn) {
      fetch("/api/actions/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          track_id: track.id,
          action: "dislike",
          atmosphere: currentScene?.id || currentScene?.atmo || "",
        }),
      }).catch(() => {});
    }
    nextTrack();
  }

  // ===== Share =====
  function handleShare(track) {
    if (!track) return;
    const url = `${window.location.origin}/track/${track.id}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        showToast("分享链接已复制", "success");
      })
      .catch(() => {
        const textarea = document.createElement("textarea");
        textarea.value = url;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        showToast("分享链接已复制", "success");
      });
    gaEvent("share", track.id);
  }

  // ===== Playlist management =====
  function addToPlaylist(track) {
    setPlaylist((prev) => {
      if (prev.find((t) => t.id === track.id)) return prev;
      return [...prev, track];
    });
  }

  function removeFromPlaylist(trackId) {
    setPlaylist((prev) => prev.filter((t) => t.id !== trackId));
  }

  function clearPlaylist() {
    setPlaylist([]);
  }

  // ===== Temp playlist management =====
  function addToTempPlaylist(track) {
    setTempPlaylist((prev) => {
      if (prev.find((t) => t.id === track.id)) return prev;
      return [...prev, track];
    });
  }

  function removeFromTempPlaylist(trackId) {
    setTempPlaylist((prev) => prev.filter((t) => t.id !== trackId));
  }

  function clearTempPlaylist() {
    setTempPlaylist([]);
  }

  async function handleCreatePlaylist(name, trackIds) {
    const token = getAuthToken();
    if (!token) return;
    try {
      const res = await fetch("/api/playlists/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name, track_ids: trackIds }),
      });
      if (!res.ok) throw new Error("create playlist failed");
      const data = await res.json();
      if (data && data.id) {
        navigate(`/playlist/${data.id}`);
      }
    } catch (e) {
      console.error("Failed to create playlist:", e);
    }
  }

  // ===== Switch track =====
  function onSwitchTrack(track) {
    const sc =
      scenes.find(
        (s) => s.id === track.scene_id || s.atmo === track.atmosphere
      ) || currentScene;
    playTrack(track, sc);
  }

  // ===== Player state persistence =====
  function fallbackRestore() {
    try {
      const saved = localStorage.getItem("fm_player_state");
      if (!saved) {
        setPlaylistSource("explore");
        return;
      }
      const state = JSON.parse(saved);

      // Version check — skip old localStorage that may have tracks without url
      if (!state._v || state._v < 2) {
        localStorage.removeItem("fm_player_state");
        setPlaylistSource("explore");
        return;
      }

      const savedSource = state.playlistSource;
      if (savedSource && savedSource !== "favorites" && savedSource !== "liked") {
        setPlaylistSource(savedSource);
      } else if (!savedSource) {
        setPlaylistSource("explore");
      }
      if (state.loopMode) setLoopMode(state.loopMode);
      if (state.track && state.track.id) {
        // If stored track has no url, fetch it fresh from API
        const trackUrl = state.track.url || state.track.audio_url;
        if (!trackUrl) {
          fetch(`/api/tracks/${state.track.id}`)
            .then((r) => r.json())
            .then((freshTrack) => {
              setCurrentTrack(freshTrack);
              const audio = audioRef.current;
              if (audio) {
                console.log('[restorePlayerState/fetched]', freshTrack.id, freshTrack.name || freshTrack.name_zh || '?', '→ URL:', freshTrack.url);
                audio.src = freshTrack.url;
                audio.volume = state.volume ?? 0.8;
              }
            })
            .catch(() => {
              setPlaylistSource("explore");
              fetchExploreNext();
            });
          return;
        }

        setCurrentTrack(state.track);
        stateRestoredRef.current = true;
        if (state.scene) setCurrentScene(state.scene);
        const audio = audioRef.current;
        if (audio) {
          console.log('[restorePlayerState]', state.track.id, state.track.name || state.track.name_zh || '?', '→ URL:', trackUrl);
          audio.src = trackUrl;
          audio.volume = state.volume ?? 0.8;
          if (state.currentTime > 0 && isFinite(state.currentTime)) {
            audio.currentTime = state.currentTime;
          }
        }
      }
    } catch (_e) {
      setPlaylistSource("explore");
      fetchExploreNext();
    }
  }

  // Initial mount — handle shared URLs / cleanup URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // 读 localStorage（SSR "Play Now" 跳转）
    const playFromLocal = localStorage.getItem('fm_play_track');

    // 如果当前在 /immersive_player 路由上——可能带有 ?track=xxx 分享参数
    if (location.pathname === "/immersive_player") {
      const immTrackId = params.get("track");
      const immPlaylistId = params.get("playlist");
      if (playFromLocal) {
        localStorage.removeItem('fm_play_track');
        fetch(`/api/tracks/${playFromLocal}`)
          .then((r) => {
            if (!r.ok) throw new Error("Track not found");
            return r.json();
          })
          .then((track) => {
            const scene = scenes.find((s) => s.category === track.category) || null;
            if (scene) setCurrentScene(scene);
            setCurrentTrack(track);
            setPlaylistSource("explore");
            const audio = audioRef.current;
            if (audio) {
              audio.src = track.url;
              audio.volume = 0.8;
              audio.play().then(() => setIsPlaying(true)).catch(() => {});
            }
            setPlayerMode("immersive");
          })
          .catch(() => {});
        return;
      }
      if (immTrackId || immPlaylistId) {
        // ImmersivePlayerPage 自己会处理，不调 fallbackRestore 以免抢播
        return;
      }
      setTimeout(() => fallbackRestore(), 0);
      return;
    }

    const sharedTrackId = params.get("track");

    // Priority: localStorage (set by SSR "Play Now" onclick)
    if (playFromLocal) {
      localStorage.removeItem('fm_play_track');
      fetch(`/api/tracks/${playFromLocal}`)
        .then((r) => {
          if (!r.ok) throw new Error("Track not found");
          return r.json();
        })
        .then((track) => {
          const scene = scenes.find((s) => s.category === track.category) || null;
          if (scene) setCurrentScene(scene);
          setCurrentTrack(track);
          sharedTrackLoaded.current = true;
          setPlaylistSource("explore");
          const audio = audioRef.current;
          if (audio) {
            audio.src = track.url;
            audio.volume = 0.8;
            audio.play().then(() => setIsPlaying(true)).catch(() => {});
          }
          setPlayerMode("immersive");
          window.history.replaceState({}, "", "/");
        })
        .catch(() => {
          setTimeout(() => fallbackRestore(), 0);
        });
      return;
    }

    // Priority: ?track=xxx (single track)
    const sharedTracks = params.get("tracks");
    if (sharedTrackId) {
      fetch(`/api/tracks/${sharedTrackId}`)
        .then((r) => {
          if (!r.ok) throw new Error("Track not found");
          return r.json();
        })
        .then((track) => {
          const scene = scenes.find((s) => s.category === track.category) || null;
          if (scene) setCurrentScene(scene);
          setCurrentTrack(track);
          sharedTrackLoaded.current = true;
          setPlaylistSource("explore");
          const audio = audioRef.current;
          if (audio) {
            console.log('[prevNext/setSharedTrack]', track.id, track.name || track.name_zh || '?', '→ URL:', track.url);
            audio.src = track.url;
            audio.volume = 0.8;
            audio.play().then(() => setIsPlaying(true)).catch(() => {});
          }
          setPlayerMode("fullscreen");
          window.history.replaceState({}, "", "/");
        })
        .catch(() => {
          fallbackRestore();
        });
      return;
    }

    // ?tracks=id1,id2,... (batch — for "play all" from playlist page)
    if (sharedTracks) {
      const ids = sharedTracks.split(",").filter(Boolean);
      if (ids.length > 0) {
        Promise.all(ids.map((id) =>
          fetch(`/api/tracks/${id}`).then((r) => r.ok ? r.json() : null)
        )).then((tracks) => {
          const valid = tracks.filter(Boolean);
          if (valid.length === 0) { fallbackRestore(); return; }
          setPlaylist(valid);
          setPlaylistSource("playlist");
          const first = valid[0];
          const scene = scenes.find((s) => s.category === first.category) || null;
          if (scene) setCurrentScene(scene);
          const audio = audioRef.current;
          if (audio) {
            audio.src = first.url;
            audio.volume = 0.8;
            audio.play().then(() => setIsPlaying(true)).catch(() => {});
          }
          setPlayerMode("fullscreen");
          window.history.replaceState({}, "", "/");
        });
        return;
      }
    }

    const sharedPlaylistId = params.get("playlist");
    if (sharedPlaylistId) {
      window.history.replaceState({}, "", "/");
      navigate(`/playlist/${sharedPlaylistId}`);
      return;
    }

    setTimeout(() => fallbackRestore(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save state on changes
  useEffect(() => {
    if (!currentTrack || !currentTrack.id) return;
    try {
      const state = {
        _v: 2, // version for migration
        track: currentTrack,
        scene: currentScene,
        playlistSource:
          playlistSource === "favorites" || playlistSource === "liked"
            ? "explore"
            : playlistSource,
        loopMode,
        volume: audioRef.current?.volume ?? 0.8,
        currentTime: audioRef.current?.currentTime ?? 0,
      };
      localStorage.setItem("fm_player_state", JSON.stringify(state));
    } catch {
      // ignore localStorage errors
    }
  }, [currentTrack, currentScene, playlistSource, loopMode]);

  // ===== Cross-tab sync: pause when another tab starts playing =====
  useEffect(() => {
    let channel = null;
    try {
      channel = new BroadcastChannel("freemusic-player-sync");
      channelRef.current = channel;

      channel.onmessage = (event) => {
        const msg = event.data;
        if (msg.type !== "play") return;
        // Another tab started playing → pause this tab's audio
        const audio = audioRef.current;
        if (audio && !audio.paused) {
          audio.pause();
          setIsPlaying(false);
          showToast?.("音乐已在其他标签页播放", "info");
        }
      };
    } catch {
      // BroadcastChannel not supported (Safari 15.2+ supports it; ignore old browsers)
    }
    return () => {
      if (channel) channel.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Broadcast to other tabs when we start playing (skip initial mount)
  const prevIsPlayingRef = useRef(false);
  useEffect(() => {
    if (isPlaying && !prevIsPlayingRef.current && channelRef.current) {
      try {
        channelRef.current.postMessage({ type: "play", trackId: currentTrack?.id });
      } catch {
        // ignore — other tab may have closed
      }
    }
    prevIsPlayingRef.current = isPlaying;
  }, [isPlaying, currentTrack?.id]);

  // Pause global audio when AmbientPage takes over playback
  useEffect(() => {
    function handleAmbientPlay() {
      const audio = audioRef.current;
      if (audio) audio.pause();
    }
    window.addEventListener('ambient-play', handleAmbientPlay);
    return () => window.removeEventListener('ambient-play', handleAmbientPlay);
  }, []);

  // Switch source: auto-play first track
  const isMounted = useRef(false);
  const prevSource = useRef(playlistSource);
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (prevSource.current === playlistSource && currentTrack) return;
    prevSource.current = playlistSource;
    setPlaylistName("");

    if (sharedTrackLoaded.current) {
      sharedTrackLoaded.current = false;
      return;
    }

    if (playlistSource === "explore") {
      // Don't auto-fetch if we just restored saved state (e.g. navigating to /profile)
      if (stateRestoredRef.current) {
        stateRestoredRef.current = false;
        return;
      }
      setTimeout(() => {
        setExploreOffset(0);
        setExploreTracks([]);
        exploreIndexRef.current = 0;
        exploreSessionId.current =
          "sess_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8);
        fetchExploreNext();
      }, 0);
      return;
    }

    const source = getSourceTracks();
    if (source.length === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentTrack(null);
      setIsPlaying(false);
      showToast(
        playlistSource === "liked"
          ? "喜欢列表为空"
          : playlistSource === "favorites"
            ? "收藏列表为空"
            : "暂无曲目",
        "info"
      );
      return;
    }
    // 同步 playlist（用于播放器面板显示曲目列表）
    if (playlistSource === "liked" || playlistSource === "favorites" || playlistSource === "temp") {
      setPlaylist(source);
    }
    const first = source[0];
    const sc = findSceneForTrack(first);
    playTrack(first, sc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistSource]);

  const value = {
    audioRef,
    currentTrack,
    currentScene,
    isPlaying,
    playlist,
    playlistSource,
    playlistName,
    loopMode,
    exploreTracks,
    exploreOffset,
    exploreSessionId,
    sharedTrackLoaded,
    playerMode,
    setCurrentTrack,
    setCurrentScene,
    setIsPlaying,
    setPlaylist,
    setPlaylistSource,
    setPlaylistName,
    setLoopMode,
    setExploreOffset,
    setPlayerMode,
    playTrack,
    togglePlay,
     
    nextTrack,
    prevTrack,
    handleEnded,
    getSourceTracks,
    findSceneForTrack,
    fetchExploreNext,
    handleDislike,
    handleShare,
    addToPlaylist,
    removeFromPlaylist,
    clearPlaylist,
    tempPlaylist,
    addToTempPlaylist,
    removeFromTempPlaylist,
    clearTempPlaylist,
    handleCreatePlaylist,
    onSwitchTrack,
  };

  return (
    <PlayerContext.Provider value={value}>
      <audio ref={audioRef} onEnded={handleEnded} />
      {children}
    </PlayerContext.Provider>
  );
}

export function usePlayerContext() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayerContext must be used within PlayerProvider");
  return ctx;
}
